import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { scanMatrix } from '@/services/api/qualification'
import type { MatrixScanResponse, MatrixScanCell, MatrixMapping } from '@/types/qualification'
import { colToLetter } from '@/utils/colLetter'

interface QualificationMatrixPickerProps {
  uploadedFileId: string
  onMappingSelected: (mapping: MatrixMapping) => void
  onScanError: (error: string) => void
}

const HEADER_KEYWORDS = new Set([
  'subject', 'subjekt', 'schulprogramm', 'klassentyp',
  'background', 'hintergrund', 'qualifikation', 'ausbildung',
  'study', 'studium',
])

export const QualificationMatrixPicker: React.FC<QualificationMatrixPickerProps> = ({
  uploadedFileId,
  onMappingSelected,
  onScanError,
}) => {
  const [scanData, setScanData] = useState<MatrixScanResponse | null>(null)
  const [subjectRow, setSubjectRow] = useState<number | null>(null)
  const [backgroundCol, setBackgroundCol] = useState<number | null>(null)
  const [subjectCol, setSubjectCol] = useState<number | null>(null)
  const [backgroundRow, setBackgroundRow] = useState<number | null>(null)
  const [isTransposed, setIsTransposed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [hoveredRow, setHoveredRow] = useState<number | null>(null)
  const [hoveredCol, setHoveredCol] = useState<number | null>(null)

  useEffect(() => {
    setLoading(true)
    scanMatrix(uploadedFileId)
      .then((data) => {
        if (!data.success) {
          onScanError(data.warnings.join(', ') || 'Scan fehlgeschlagen')
          return
        }
        setScanData(data)
        if (data.suggested_subject_row) setSubjectRow(data.suggested_subject_row)
        if (data.suggested_background_col) setBackgroundCol(data.suggested_background_col)
      })
      .catch((err) => {
        onScanError(err instanceof Error ? err.message : 'Scan fehlgeschlagen')
      })
      .finally(() => setLoading(false))
  }, [uploadedFileId, onScanError])

  const cellMap = useMemo(() => {
    const map = new Map<string, MatrixScanCell>()
    scanData?.cells.forEach((c) => map.set(`${c.row}-${c.col}`, c))
    return map
  }, [scanData?.cells])

  const findFirstDataCol = useCallback((): number => {
    if (!backgroundCol || !subjectRow || !scanData) return (backgroundCol || 0) + 1
    for (let c = backgroundCol + 1; c <= scanData.max_col; c++) {
      const cell = cellMap.get(`${subjectRow}-${c}`)
      if (cell?.value) return c
    }
    return backgroundCol + 1
  }, [backgroundCol, subjectRow, scanData, cellMap])

  const findFirstDataRow = useCallback((): number => {
    if (!subjectCol || !backgroundRow || !scanData) return (backgroundRow || 0) + 1
    for (let r = backgroundRow + 1; r <= scanData.max_row; r++) {
      const cell = cellMap.get(`${r}-${subjectCol}`)
      if (cell?.value) return r
    }
    return backgroundRow + 1
  }, [subjectCol, backgroundRow, scanData, cellMap])

  const computedMapping: MatrixMapping | null = useMemo(() => {
    if (!scanData) return null
    if (isTransposed) {
      if (!subjectCol || !backgroundRow) return null
      return {
        subject_header_row: backgroundRow,
        background_header_col: subjectCol,
        data_start_row: backgroundRow + 1,
        data_start_col: subjectCol + 1,
      }
    }
    if (!subjectRow || !backgroundCol) return null
    return {
      subject_header_row: subjectRow,
      background_header_col: backgroundCol,
      data_start_row: subjectRow + 1,
      data_start_col: findFirstDataCol(),
    }
  }, [isTransposed, subjectRow, backgroundCol, subjectCol, backgroundRow, scanData, findFirstDataCol])

  const isHeaderKeyword = (val: string | null | undefined): boolean => {
    return val !== null && val !== undefined && HEADER_KEYWORDS.has(val.toLowerCase())
  }

  const bgCount = useMemo(() => {
    if (!scanData) return 0
    if (isTransposed) {
      if (!backgroundRow || !subjectCol) return 0
      let count = 0
      for (let c = subjectCol + 1; c <= scanData.max_col; c++) {
        const cell = cellMap.get(`${backgroundRow}-${c}`)
        if (cell?.value && !isHeaderKeyword(cell.value)) count++
      }
      return count
    }
    if (!subjectRow || !backgroundCol) return 0
    let count = 0
    for (let r = subjectRow + 1; r <= scanData.max_row; r++) {
      const cell = cellMap.get(`${r}-${backgroundCol}`)
      if (cell?.value && !isHeaderKeyword(cell.value)) count++
    }
    return count
  }, [isTransposed, subjectRow, backgroundCol, subjectCol, backgroundRow, scanData, cellMap])

  const subjCount = useMemo(() => {
    if (!scanData) return 0
    if (isTransposed) {
      if (!backgroundRow || !subjectCol) return 0
      const dataStartRow = findFirstDataRow()
      let count = 0
      for (let r = dataStartRow; r <= scanData.max_row; r++) {
        const cell = cellMap.get(`${r}-${subjectCol}`)
        if (cell?.value && !isHeaderKeyword(cell.value)) count++
      }
      return count
    }
    if (!subjectRow || !backgroundCol) return 0
    const dataStartCol = findFirstDataCol()
    let count = 0
    for (let c = dataStartCol; c <= scanData.max_col; c++) {
      const cell = cellMap.get(`${subjectRow}-${c}`)
      if (cell?.value && !isHeaderKeyword(cell.value)) count++
    }
    return count
  }, [isTransposed, subjectRow, backgroundCol, subjectCol, backgroundRow, scanData, cellMap, findFirstDataCol, findFirstDataRow])

  const handleCellClick = (row: number, col: number) => {
    if (isTransposed) {
    if (row === backgroundRow && col === subjectCol) {
      setSubjectCol(null)
      setBackgroundRow(null)
      return
    }
    if (subjectCol !== null && col === subjectCol && row !== backgroundRow) {
      setSubjectCol(null)
      return
    }
    if (backgroundRow !== null && row === backgroundRow && col !== subjectCol) {
      setBackgroundRow(null)
      return
    }
    if (backgroundRow !== null && row > backgroundRow) {
      setSubjectCol(col)
    } else if (subjectCol !== null && col !== subjectCol) {
      setBackgroundRow(row)
    } else {
      setSubjectCol(col)
    }
      return
    }

    if (row === subjectRow && col === backgroundCol) {
      setSubjectRow(null)
      setBackgroundCol(null)
      return
    }
    if (row === subjectRow && col !== backgroundCol) {
      setSubjectRow(null)
      return
    }
    if (col === backgroundCol && row !== subjectRow) {
      setBackgroundCol(null)
      return
    }
    if (backgroundCol && col > backgroundCol) {
      setSubjectRow(row)
    } else if (subjectRow && row > subjectRow) {
      setBackgroundCol(col)
    } else {
      setSubjectRow(row)
    }
  }

  const getCellStyle = (row: number, col: number): string => {
    const isHoveredRow = row === hoveredRow && row !== (isTransposed ? backgroundRow : subjectRow)
    const isHoveredCol = col === hoveredCol && col !== (isTransposed ? subjectCol : backgroundCol)

    if (isTransposed) {
      const isSubjectCol = col === subjectCol
      const isBackgroundRow = row === backgroundRow
      const isDataArea =
        subjectCol !== null &&
        backgroundRow !== null &&
        row > backgroundRow &&
        col > subjectCol

      if (isSubjectCol && isBackgroundRow) return 'bg-gray-200 font-medium text-center'
      if (isSubjectCol) return 'bg-blue-100 border-blue-300 font-medium cursor-pointer'
      if (isBackgroundRow) return 'bg-purple-100 border-purple-300 font-medium cursor-pointer'
      if (isDataArea) return 'bg-green-50'
      if (isHoveredRow) return 'bg-purple-50 cursor-pointer'
      if (isHoveredCol) return 'bg-blue-50 cursor-pointer'
      return ''
    }

    const isSubjectRow = row === subjectRow
    const isBackgroundCol = col === backgroundCol
    const isDataArea =
      subjectRow !== null &&
      backgroundCol !== null &&
      row > subjectRow &&
      col > backgroundCol

    if (isSubjectRow && isBackgroundCol) return 'bg-gray-200 font-medium text-center'
    if (isSubjectRow) return 'bg-blue-100 border-blue-300 font-medium cursor-pointer'
    if (isBackgroundCol) return 'bg-purple-100 border-purple-300 font-medium cursor-pointer'
    if (isDataArea) return 'bg-green-50'
    if (isHoveredRow) return 'bg-blue-50 cursor-pointer'
    if (isHoveredCol) return 'bg-purple-50 cursor-pointer'
    return ''
  }

  const colLetters = useMemo(() => {
    const letters: string[] = []
    for (let c = 1; c <= (scanData?.max_col || 0); c++) {
      letters.push(colToLetter(c))
    }
    return letters
  }, [scanData?.max_col])

  if (loading) {
    return (
      <div className="text-center p-8">
        <div className="inline-block animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mb-4" />
        <p className="text-gray-500">Datei wird analysiert...</p>
      </div>
    )
  }

  if (!scanData) {
    return null
  }

  if (scanData.cells.length === 0) {
    return (
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
          Keine Zellen mit Inhalt gefunden. Die Datei scheint leer zu sein.
        </div>
        {scanData.warnings.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-yellow-800 font-medium mb-1">Warnungen</p>
            <ul className="text-yellow-700 text-sm list-disc list-inside">
              {scanData.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-800">
          <strong>Matrix zuordnen:</strong> Klicke auf eine Zelle in der Fach-Zeile (Subjects, blau)
          und eine Zelle in der Ausbildungs-Spalte (Backgrounds, purpur).
          Klick auf eine bereits gewählte Zeile/Spalte hebt die Auswahl auf.
          Der grüne Bereich zeigt die Qualifikations-Daten.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
        <input
          type="checkbox"
          checked={isTransposed}
          onChange={(e) => {
            setIsTransposed(e.target.checked)
            setSubjectRow(null)
            setBackgroundCol(null)
            setSubjectCol(null)
            setBackgroundRow(null)
          }}
          className="rounded border-gray-300"
        />
        <span>Matrix drehen (Subjects in Spalte, Backgrounds in Zeile)</span>
      </label>

      <div className="overflow-x-auto border rounded-lg">
        <table className="border-collapse text-xs">
          <thead>
            <tr>
              <th className="border p-1 bg-gray-100 w-8 text-center"></th>
              {colLetters.map((letter, ci) => {
                const col = ci + 1
                const isHighlight = isTransposed
                  ? col === subjectCol
                  : col === backgroundCol
                return (
                  <th
                    key={ci}
                    className={`border p-1 bg-gray-100 text-center min-w-[60px] ${
                      isHighlight ? (isTransposed ? 'bg-blue-200' : 'bg-purple-200') : ''
                    }`}
                  >
                    {letter}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: scanData.max_row }, (_, ri) => {
              const row = ri + 1
              const isRowHighlight = isTransposed
                ? row === backgroundRow
                : row === subjectRow
              return (
                <tr
                  key={row}
                  onMouseEnter={() => setHoveredRow(row)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td
                    className={`border p-1 bg-gray-100 text-center font-medium ${
                      isRowHighlight ? (isTransposed ? 'bg-purple-200' : 'bg-blue-200') : ''
                    }`}
                  >
                    {row}
                  </td>
                  {colLetters.map((_letter, ci) => {
                    const col = ci + 1
                    const cell = cellMap.get(`${row}-${col}`)
                    return (
                      <td
                        key={col}
                        className={`border p-1 text-center truncate max-w-[120px] ${getCellStyle(row, col)}`}
                        onClick={() => handleCellClick(row, col)}
                        onMouseEnter={() => setHoveredCol(col)}
                        onMouseLeave={() => setHoveredCol(null)}
                        title={cell?.value || ''}
                      >
                        {cell?.value
                          ? cell.value.length > 15
                            ? cell.value.slice(0, 15) + '...'
                            : cell.value
                          : ''}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-4 text-sm">
        {isTransposed ? (
          <>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
              <span>Subjects: Spalte {subjectCol ? colToLetter(subjectCol) : '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-100 border border-purple-300 rounded"></div>
              <span>Backgrounds: Zeile {backgroundRow ?? '—'}</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
              <span>Subjects: Zeile {subjectRow ?? '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-100 border border-purple-300 rounded"></div>
              <span>Backgrounds: Spalte {backgroundCol ? colToLetter(backgroundCol) : '—'}</span>
            </div>
          </>
        )}
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-50 border border-green-200 rounded"></div>
          <span>{bgCount} Backgrounds, {subjCount} Subjects</span>
        </div>
      </div>

      {scanData.warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-yellow-800 font-medium mb-1">Warnungen</p>
          <ul className="text-yellow-700 text-sm list-disc list-inside">
            {scanData.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <button
        onClick={() => computedMapping && onMappingSelected(computedMapping)}
        disabled={!computedMapping || bgCount === 0 || subjCount === 0}
        className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Vorschau laden
      </button>
    </div>
  )
}
